import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { workService } from '../services/workService';
import { Task, TaskStatus, TASK_ROW_BORDER } from '../types/work';
import DataTable from '../components/common/DataTable';
import { TaskCard } from '../components/work/TaskCard';
import { WorkStatusBadge } from '../components/work/WorkStatusBadge';
import { PriorityChip } from '../components/work/PriorityChip';
import { DueDateLabel } from '../components/work/DueDateLabel';
import { ViewToggle } from '../components/work/ViewToggle';
import { FilterPanel } from '../components/work/FilterPanel';
import { QuickFilterChips } from '../components/work/QuickFilterChips';
import { ContextStrip } from '../components/work/ContextStrip';
import UserBadge from '../components/common/UserBadge';
import { FiPlus, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import { differenceInDays } from 'date-fns';
import { TaskDetailPanel } from '../components/work/TaskDetailPanel';
import CreateWorkItemModal from '../components/work/CreateWorkItemModal';

const Tasks: React.FC = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const projectFilter = searchParams.get('project');
  const goalFilter = searchParams.get('goal');
  const [view, setView] = useState<'cards' | 'table'>('table'); // table is primary for tasks
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [quickFilter, setQuickFilter] = useState<string | null>(null);
  const [panelTaskId, setPanelTaskId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const { data: tasks = [], isLoading, isError, refetch } = useQuery(
    ['tasks', projectFilter, goalFilter],
    () => workService.getTasks({
      ...(projectFilter ? { project_id: projectFilter } : {}),
      ...(goalFilter ? { goal_id: goalFilter } : {}),
    }),
  );

  // Optimistic status update
  const statusMutation = useMutation(
    ({ taskId, status }: { taskId: string; status: TaskStatus }) =>
      workService.updateTask(taskId, { status }),
    {
      onMutate: async ({ taskId, status }) => {
        await queryClient.cancelQueries(['tasks']);
        const prev = queryClient.getQueryData<Task[]>(['tasks', projectFilter, goalFilter]);
        queryClient.setQueryData(['tasks', projectFilter, goalFilter],
          prev?.map(t => t.id === taskId ? { ...t, status } : t)
        );
        return { prev };
      },
      onError: (_err, _vars, context) => {
        queryClient.setQueryData(['tasks', projectFilter, goalFilter], context?.prev);
      },
      onSettled: () => {
        queryClient.invalidateQueries(['tasks']);
      },
    },
  );

  const handleStatusChange = useCallback((taskId: string, newStatus: TaskStatus) => {
    statusMutation.mutate({ taskId, status: newStatus });
  }, [statusMutation]);

  // Quick filter logic
  const now = new Date();
  const overdueTasks = tasks.filter(t => t.due_date && new Date(t.due_date) < now && t.status !== 'completed' && t.status !== 'cancelled');
  const blockedTasks = tasks.filter(t => t.status === 'blocked');
  const dueThisWeek = tasks.filter(t => {
    if (!t.due_date) return false;
    const d = differenceInDays(new Date(t.due_date), now);
    return d >= 0 && d <= 7 && t.status !== 'completed';
  });

  const filtered = useMemo(() => {
    let result = tasks;
    if (statusFilter.length) result = result.filter(t => statusFilter.includes(t.status));
    if (quickFilter === 'overdue') result = overdueTasks;
    if (quickFilter === 'blocked') result = blockedTasks;
    if (quickFilter === 'due_this_week') result = dueThisWeek;
    return result;
  }, [tasks, statusFilter, quickFilter, overdueTasks, blockedTasks, dueThisWeek]);

  const quickChips = [
    { label: 'Overdue', count: overdueTasks.length, active: quickFilter === 'overdue', onClick: () => setQuickFilter(quickFilter === 'overdue' ? null : 'overdue') },
    { label: 'Blocked', count: blockedTasks.length, active: quickFilter === 'blocked', onClick: () => setQuickFilter(quickFilter === 'blocked' ? null : 'blocked') },
    { label: 'Due this week', count: dueThisWeek.length, active: quickFilter === 'due_this_week', onClick: () => setQuickFilter(quickFilter === 'due_this_week' ? null : 'due_this_week') },
  ];

  // Overdue tier for row styling
  const getRowClass = (task: Task) => {
    const border = TASK_ROW_BORDER[task.status] || '';
    if (task.status === 'completed') return `${border} opacity-60`;
    if (task.due_date) {
      const overdueDays = differenceInDays(now, new Date(task.due_date));
      if (overdueDays >= 4) return `${border} bg-red-900/[0.04]`;
    }
    return border;
  };

  const columns: ColumnDef<Task, any>[] = [
    {
      accessorKey: 'title',
      header: 'Task',
      cell: ({ row }) => {
        const t = row.original;
        return (
          <div>
            <span className={`font-medium ${t.status === 'completed' ? 'line-through text-gray-500' : ''}`}>
              {t.title}
            </span>
            {(t.goal_id || t.project_id) && (
              <div className="text-xs text-gray-500 mt-0.5">
                ↳ {t.goal_id && <span className="text-emerald-400/70">Goal</span>}
                {t.goal_id && t.project_id && ' / '}
                {t.project_id && <span className="text-teal-400/70">Project</span>}
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <WorkStatusBadge
          status={row.original.status}
          clickable
          onCycle={(newStatus) => handleStatusChange(row.original.id, newStatus)}
        />
      ),
    },
    { accessorKey: 'priority', header: 'Priority', cell: ({ row }) => <PriorityChip priority={row.original.priority} /> },
    { accessorKey: 'due_date', header: 'Due', cell: ({ row }) => <DueDateLabel date={row.original.due_date} /> },
    { accessorKey: 'owner', header: 'Owner', cell: ({ row }) => <UserBadge owner={row.original.owner} size="sm" /> },
  ];

  return (
    <div className="space-y-4">
      {showCreate && <CreateWorkItemModal entityType="task" onClose={() => setShowCreate(false)} defaults={projectFilter ? { project_id: projectFilter } : goalFilter ? { goal_id: goalFilter } : undefined} />}
      {panelTaskId && <TaskDetailPanel taskId={panelTaskId} onClose={() => setPanelTaskId(null)} />}

      {/* Context strip */}
      {(projectFilter || goalFilter) && (
        <ContextStrip
          label={projectFilter ? `Project ${projectFilter.slice(0, 8)}...` : `Goal ${goalFilter?.slice(0, 8)}...`}
          icon={projectFilter ? '📁' : '🎯'}
          accentColor={projectFilter ? 'teal' : 'emerald'}
          onDismiss={() => setSearchParams({})}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">
            Tasks
          </h1>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Discrete work items with assignees and due dates
          </p>
        </div>
        <div className="flex items-center gap-3">
          <FilterPanel activeCount={statusFilter.length} onClear={() => { setStatusFilter([]); setQuickFilter(null); }}>
            <div className="space-y-1">
              <label className="text-xs text-gray-400">Status</label>
              <div className="flex flex-wrap gap-1">
                {['todo', 'in_progress', 'in_review', 'blocked', 'completed', 'cancelled'].map(s => (
                  <button key={s} onClick={() => setStatusFilter(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s])}
                    className={`px-2 py-0.5 rounded text-xs ${statusFilter.includes(s) ? 'bg-violet-900/40 text-violet-300' : darkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'}`}
                  >{s.replace(/_/g, ' ')}</button>
                ))}
              </div>
            </div>
          </FilterPanel>
          <ViewToggle view={view} onChange={setView} />
          <button onClick={() => setShowCreate(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-violet-600 to-purple-500 text-white text-sm font-medium hover:shadow-lg hover:shadow-violet-500/20 transition-shadow">
            <FiPlus className="w-3.5 h-3.5" /> New Task
          </button>
        </div>
      </div>

      {/* Quick filter chips */}
      <QuickFilterChips chips={quickChips} />

      {/* Content */}
      {isError ? (
        <div className="flex flex-col items-center py-20">
          <FiAlertCircle className="w-12 h-12 text-red-400 mb-4" />
          <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Failed to load tasks</h3>
          <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Check your connection and try again.</p>
          <button onClick={() => refetch()} className="px-4 py-2 rounded-lg bg-gray-700 text-white text-sm hover:bg-gray-600">Retry</button>
        </div>
      ) : isLoading ? (
        <div className="flex justify-center py-20"><div className="animate-spin w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full" /></div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center py-20">
          <FiCheckCircle className={`w-12 h-12 mb-4 ${darkMode ? 'text-violet-400/40' : 'text-violet-300'}`} />
          <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            {quickFilter || statusFilter.length ? 'No tasks match your filters' : 'Nothing to work on.'}
          </h3>
          <p className={`text-sm mb-6 max-w-md text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {quickFilter === 'blocked' ? 'No blocked tasks. Everything is flowing.' :
             quickFilter === 'overdue' ? 'Nothing overdue. You\'re on track!' :
             'Create a task to get started.'}
          </p>
        </div>
      ) : view === 'cards' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(task => (
            <TaskCard
              key={task.id}
              task={task}
              onClick={() => setPanelTaskId(task.id)}
              onStatusChange={(newStatus) => handleStatusChange(task.id, newStatus)}
            />
          ))}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={filtered}
          onRowClick={(task: Task) => setPanelTaskId(task.id)}
          searchable
          searchPlaceholder="Search tasks..."
          emptyMessage="No tasks match your filters"
        />
      )}
    </div>
  );
};

export default Tasks;
